// Keep facings and x-window; restack shelf tiers to the declared order.
import {
  GeneratedLayout,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  effectiveAttrs,
} from "../studioTypes";
import { DEFAULT_HIERARCHY_MOVE_RULES, HierarchyMoveRules } from "./hierarchyMoveRules";
import { cloneLayout, dealAcrossBand, laneSegmentsOf, openingsOf } from "./layoutOps";
import { RehomeRequest, resolveScope } from "./rehomeBlock";

export interface RebandResult {
  layout: GeneratedLayout;
  /** Scope band values, declared order top-first (the contract). */
  declaredOrder: readonly string[];
  /** Band values in the previous top-first reading order. */
  previousOrder: readonly string[];
  /** The re-tiered bands: value → shelves, top first. */
  bands: readonly { value: string; shelves: readonly number[] }[];
  scopeGeneratorKey: string;
  /** Lanes whose x was re-flowed (everything else is verbatim). */
  touchedLanes: number;
}

/** Values at `attr` are bands when their shelf sets are near-disjoint tiers; columns share shelves. */
export const isBandedScope = (
  scopeItems: readonly StudioItem[],
  draft: StudioDraft,
  displayed: GeneratedLayout,
  attr: string,
): boolean => {
  const shelvesOf = new Map<string, Set<number>>();
  for (const item of scopeItems) {
    const value = String(effectiveAttrs(item, draft)[attr] ?? "");
    if (!value || value === "?") continue;
    const set = shelvesOf.get(value) ?? new Set<number>();
    for (const segment of displayed.placements[item.skuId]?.segments ?? []) {
      set.add(segment.shelf);
    }
    shelvesOf.set(value, set);
  }
  const sets = [...shelvesOf.values()].filter((set) => set.size > 0);
  if (sets.length < 2) return false;
  for (let a = 0; a < sets.length; a++) {
    for (let b = a + 1; b < sets.length; b++) {
      let shared = 0;
      for (const shelf of sets[a]) if (sets[b].has(shelf)) shared += 1;
      // More than one shared shelf means columns, not tiers (one wrap shelf is tolerated).
      if (shared > 1) return false;
    }
  }
  return true;
};

export const rebandScope = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  displayed: GeneratedLayout,
  request: RehomeRequest,
  rules: HierarchyMoveRules = DEFAULT_HIERARCHY_MOVE_RULES,
): RebandResult | null => {
  void rules;
  const scope = resolveScope(items, draft, request.scopeKey, request.attr);
  if (!scope) return null;
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  const valueOf = (skuId: string): string => {
    const item = bySku.get(skuId);
    return item ? String(effectiveAttrs(item, draft)[request.attr] ?? "") : "";
  };
  const scopeSkus = new Set(scope.scopeItems.map((item) => item.skuId));

  // A cross-run scope has no single tier stack to re-tier.
  const runIds = new Set(scope.scopeItems.map((item) => item.run));
  if (runIds.size !== 1) return null;
  const runId = [...runIds][0];
  const run = runs.find((candidate) => candidate.run === runId);
  if (!run) return null;

  // Per-value shelf sets and widths, ordered by mean shelf rank (rank 1 is the top shelf).
  const widthOf = new Map(items.map((item) => [item.skuId, item.facingWidthCm]));
  const heightOf = (skuId: string): number => bySku.get(skuId)?.heightCm ?? 0;
  const perValue = new Map<string, { shelves: Set<number>; width: number; meanShelf: number }>();
  for (const item of scope.scopeItems) {
    const value = valueOf(item.skuId);
    if (!value || value === "?") continue;
    const entry = perValue.get(value) ?? { shelves: new Set<number>(), width: 0, meanShelf: 0 };
    for (const segment of displayed.placements[item.skuId]?.segments ?? []) {
      if (segment.run !== runId) continue;
      entry.shelves.add(segment.shelf);
      entry.width += segment.hFacings * (widthOf.get(item.skuId) ?? 0);
    }
    perValue.set(value, entry);
  }
  for (const entry of perValue.values()) {
    const ranks = [...entry.shelves];
    entry.meanShelf = ranks.length ? ranks.reduce((a, b) => a + b, 0) / ranks.length : Infinity;
  }
  const previousOrder = [...perValue.entries()]
    .filter(([, entry]) => entry.shelves.size > 0)
    .sort((a, b) => a[1].meanShelf - b[1].meanShelf)
    .map(([value]) => value);
  if (previousOrder.length < 2 || !previousOrder.includes(request.value)) return null;

  // Declared order: rest-filter-splice, exactly the slide's contract.
  const rest = previousOrder.filter((value) => value !== request.value);
  const at = Math.max(0, Math.min(rest.length, request.destIndex));
  const declaredOrder = [...rest];
  declaredOrder.splice(at, 0, request.value);

  // Each band keeps its measured shelf count; a shared shelf counts for the wider band.
  const pool = [...new Set([...perValue.values()].flatMap((entry) => [...entry.shelves]))].sort(
    (a, b) => a - b,
  );
  const countOf = new Map<string, number>();
  for (const value of previousOrder) countOf.set(value, 0);
  for (const shelf of pool) {
    let best: string | null = null;
    let bestWidth = -1;
    for (const [value, entry] of perValue) {
      if (!entry.shelves.has(shelf)) continue;
      let width = 0;
      for (const item of scope.scopeItems) {
        if (valueOf(item.skuId) !== value) continue;
        for (const segment of displayed.placements[item.skuId]?.segments ?? []) {
          if (segment.run === runId && segment.shelf === shelf) {
            width += segment.hFacings * (widthOf.get(item.skuId) ?? 0);
          }
        }
      }
      if (width > bestWidth) {
        bestWidth = width;
        best = value;
      }
    }
    if (best !== null) countOf.set(best, (countOf.get(best) ?? 0) + 1);
  }
  // Every band keeps at least one shelf.
  for (const value of previousOrder) {
    if ((countOf.get(value) ?? 0) === 0) {
      const donor = [...countOf.entries()].sort((a, b) => b[1] - a[1])[0];
      if (!donor || donor[1] <= 1) return null;
      countOf.set(donor[0], donor[1] - 1);
      countOf.set(value, 1);
    }
  }

  // Re-tier: walk the pool top-first, handing each band its shelf count in declared order.
  let cursor = 0;
  const bands = declaredOrder.map((value) => {
    const shelves = pool.slice(cursor, cursor + (countOf.get(value) ?? 0));
    cursor += countOf.get(value) ?? 0;
    return { value, shelves };
  });
  if (cursor !== pool.length) return null;

  // Height gate: every band needs at least one item that clears each of its new shelves.
  const openings = openingsOf(run);
  for (const band of bands) {
    const own = scope.scopeItems.filter((item) => valueOf(item.skuId) === band.value);
    for (const shelf of band.shelves) {
      const opening = openings.get(shelf) ?? Infinity;
      if (!own.some((item) => item.heightCm <= opening + 0.05)) return null;
    }
  }

  // Execute on a clone: re-deal each band across its new shelves, then the bounded x re-flow.
  const layout = cloneLayout(displayed);
  const lanesBefore = laneSegmentsOf(displayed, items);
  const scopeWidthBefore = new Map<number, { width: number; left: number }>();
  for (const shelf of pool) {
    const lane = lanesBefore.get(`${runId}|${shelf}`) ?? [];
    const mine = lane.filter((entry) => scopeSkus.has(entry.skuId));
    scopeWidthBefore.set(shelf, {
      width: mine.reduce((sum, entry) => sum + entry.widthCm, 0),
      left: mine.length ? Math.min(...mine.map((entry) => entry.segment.xCm)) : NaN,
    });
  }
  // The window's left edge, constant down the parent block's full span.
  const windowLeft = Math.min(
    ...[...scopeWidthBefore.values()].map((entry) => entry.left).filter((x) => !Number.isNaN(x)),
  );

  const cloneLanes = laneSegmentsOf(layout, items);
  for (const band of bands) {
    const segments = [...cloneLanes.values()]
      .flat()
      .filter(
        (entry) =>
          scopeSkus.has(entry.skuId) &&
          entry.segment.run === runId &&
          valueOf(entry.skuId) === band.value,
      );
    dealAcrossBand(segments, band.shelves, run, heightOf);
  }

  // Bounded x re-flow per touched lane.
  const lanesAfter = laneSegmentsOf(layout, items);
  let touchedLanes = 0;
  for (const shelf of pool) {
    const lane = (lanesAfter.get(`${runId}|${shelf}`) ?? []).sort(
      (a, b) => a.segment.xCm - b.segment.xCm,
    );
    const mine = lane.filter((entry) => scopeSkus.has(entry.skuId));
    const before = scopeWidthBefore.get(shelf) ?? { width: 0, left: windowLeft };
    const left = Number.isNaN(before.left) ? windowLeft : before.left;
    const newWidth = mine.reduce((sum, entry) => sum + entry.widthCm, 0);
    const delta = newWidth - before.width;
    // Outside segments right of the window shift by the width delta; left of it, untouched.
    for (const entry of lane) {
      if (scopeSkus.has(entry.skuId)) continue;
      if (entry.segment.xCm >= left) entry.segment.xCm = +(entry.segment.xCm + delta).toFixed(2);
    }
    let x = left;
    for (const entry of mine) {
      entry.segment.xCm = +x.toFixed(2);
      x += entry.widthCm;
    }
    touchedLanes += 1;
  }

  return {
    layout,
    declaredOrder,
    previousOrder,
    bands,
    scopeGeneratorKey: scope.scopeGeneratorKey,
    touchedLanes,
  };
};
